if (!customElements.get('quick-order-list-container')) {
  customElements.define(
    'quick-order-list-container',
    class QuickOrderListContainer extends HTMLElement {
      constructor() {
        super();

        document.addEventListener('cart:bundled-sections', this.onPrepareBundledSections.bind(this));
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      onPrepareBundledSections(event) {
        event.detail.sections.push(this.sectionId);
      }
    }
  );
}

if (!customElements.get('quick-order-list-remove-button')) {
  customElements.define(
    'quick-order-list-remove-button',
    class QuickOrderListRemoveButton extends HTMLAnchorElement {
      constructor() {
        super();

        this.addEventListener('click', this.onButtonClick);
      }

      get quickOrderList() {
        return this.closest('form[is="quick-order-list"]');
      }

      onButtonClick(event) {
        event.preventDefault();
        this.quickOrderList.updateQuantity(this.getAttribute('data-index'), 0);
      }
    },
    { extends: 'a' }
  );
}

if (!customElements.get('quick-order-list-remove-all-button')) {
  customElements.define(
    'quick-order-list-remove-all-button',
    class QuickOrderListRemoveAllButton extends HTMLElement {
      constructor() {
        super();
        console.log('quick-order-list-remove-all-button initialized');
        this.actions = {
          confirm: 'confirm',
          remove: 'remove',
          removeAll: 'removeAll',
          cancel: 'cancel',
        };

        this.addEventListener('click', this.onButtonClick);
      }

      get quickOrderList() {
        return this.closest('form[is="quick-order-list"]');
      }

      get allVariants() {
        return this.quickOrderList.querySelectorAll('[data-quantity-variant-id]');
      }

      get items() {
        const items = {};
        this.allVariants.forEach((variant) => {
          const cartQty = parseInt(variant.getAttribute('data-cart-quantity'));
          if (cartQty > 0) {
            this.hasVariantsInCart = true;
            items[parseInt(variant.getAttribute('data-quantity-variant-id'))] = 0;
          }
        });
        return items;
      }

      onButtonClick(event) {
        event.preventDefault();

        if (this.getAttribute('data-action') === this.actions.confirm) {
          this.toggleConfirmation(false, true);
        } else if (this.getAttribute('data-action') === this.actions.remove) {
          this.quickOrderList.updateMultipleQty(this.items);
          this.toggleConfirmation(true, false);
        } else if (this.getAttribute('data-action') === this.actions.removeAll) {
          this.quickOrderList.removeAllItemsInCart(this.items);
          this.toggleConfirmation(true, false);
        } else if (this.getAttribute('data-action') === this.actions.cancel) {
          this.toggleConfirmation(true, false);
        }
      }

      toggleConfirmation(showConfirmation, showInfo) {
        this.quickOrderList
          .querySelector('.quick-order-list-total__confirmation')
          .toggleAttribute('hidden', showConfirmation);
        this.quickOrderList.querySelector('.quick-order-list-total__info').toggleAttribute('hidden', showInfo);
      }
    }
  );
}

if (!customElements.get('quick-order-list')) {
  customElements.define(
    'quick-order-list',
    class QuickOrderList extends HTMLFormElement {
      cartUpdateUnsubscriber = undefined;
      updateQueue = [];
      isProcessingQueue = false;
      pendingUpdates = new Map(); // Store pending updates by variant ID

      constructor() {
        super();

        this.addEventListener('change', theme.utils.debounce(this.onChange.bind(this), 300));
        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          this.onCartUpdate.bind(this)
        );
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get allVariants() {
        return this.querySelectorAll('[data-quantity-variant-id]');
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      onChange(event) {
        const target = event.target;
        const inputValue = parseInt(target.value);
        const index = target.getAttribute('data-index');
        const variantId = target.getAttribute('data-quantity-variant-id');

        // Store the pending update
        this.pendingUpdates.set(variantId, { index, value: inputValue, target, name: target.getAttribute('name') });

        if (inputValue === 0) {
          this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
        } else {
          this.validateQuantity(event);
        }
      }

      onCartUpdate(event) {
        if (event.cart.errors) {
          this.onCartError(event.cart.errors, event.target);
          this.isProcessingQueue = false;
          this.processNextInQueue();
          return;
        }

        // Store current input states before DOM update
        const inputStates = this.captureInputStates();

        const renderSectionId = this.getAttribute('data-render-section-id') || this.sectionId;
        let htmlString = event.cart.sections[renderSectionId];
        if (this.getAttribute('data-render-section-id')) {
          htmlString = htmlString.replaceAll(renderSectionId, this.sectionId);
        }

        const sectionToRender = new DOMParser().parseFromString(htmlString, 'text/html');

        // Only update specific line if line is provided, otherwise update all
        if (event.line) {
          // Update specific variant item
          const currentLineItem = this.querySelector(`#VariantItem-${this.sectionId}-${event.line}`);
          const updatedLineItem = sectionToRender.querySelector(`#VariantItem-${this.sectionId}-${event.line}`);

          if (currentLineItem && updatedLineItem) {
            currentLineItem.innerHTML = updatedLineItem.innerHTML;
          }

          // Update totals section
          const currentTotal = this.querySelector('.quick-order-list__total');
          const updatedTotal = sectionToRender.querySelector('.quick-order-list__total');

          if (currentTotal && updatedTotal) {
            currentTotal.innerHTML = updatedTotal.innerHTML;
          }
        } else {
          // Fallback: Full update for operations without specific line (like removeAll)
          const miniCart = document.querySelector(`#MiniCart-${this.sectionId}`);
          if (miniCart) {
            const updatedElement = sectionToRender.querySelector(`#MiniCart-${this.sectionId}`);
            if (updatedElement) {
              miniCart.innerHTML = updatedElement.innerHTML;
            }
          }

          const mainCart = document.querySelector(`#MainCart-${this.sectionId}`);
          if (mainCart) {
            const updatedElement = sectionToRender.querySelector(`#MainCart-${this.sectionId}`);
            if (updatedElement) {
              mainCart.innerHTML = updatedElement.innerHTML;
            }
          }
        }

        // Restore input states after DOM update
        this.restoreInputStates(inputStates);

        const lineItem = document.getElementById(`VariantItem-${this.sectionId}-${event.line}`);
        if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
          theme.a11y.trapFocus(this, lineItem.querySelector(`[name="${event.name}"]`));
        } else if (lineItem) {
          theme.a11y.trapFocus(this, lineItem.querySelector('.variant-item__title'));
        }

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: {
              cart: event.cart,
            },
          })
        );

        // Mark this update as complete and process next in queue
        this.isProcessingQueue = false;
        this.processNextInQueue();
      }

      onCartError(errors, target) {
        if (target) {
          this.disableLoading(target.getAttribute('data-index'));
          this.setValidity(target, errors);
          return;
        } else {
          window.location.href = theme.routes.cart_url;
        }

        alert(errors);
      }

      updateQuantity(line, quantity, name, target) {
        const items = {};
        items[line] = quantity;

        this.queueUpdate({ items, line, name, target });
      }

      queueUpdate(updateData) {
        // Enable loading immediately when adding to queue
        if (updateData.line) {
          this.enableLoading(updateData.line);
        }

        // Add to queue
        this.updateQueue.push(updateData);

        // Process queue if not already processing
        if (!this.isProcessingQueue) {
          this.processNextInQueue();
        }
      }

      processNextInQueue() {
        // If queue is empty or already processing, return
        if (this.updateQueue.length === 0 || this.isProcessingQueue) {
          return;
        }

        // Mark as processing
        this.isProcessingQueue = true;

        // Get next update from queue
        const updateData = this.updateQueue.shift();

        // Execute the update
        if (updateData.isRemoveAll) {
          this.executeRemoveAll();
        } else {
          this.executeUpdate(updateData.items, updateData.line, updateData.name, updateData.target);
        }
      }

      captureInputStates() {
        const states = new Map();

        // Capture loader states
        const loaderStates = new Map();
        const allLoaders = this.querySelectorAll(`[id^="Loader-${this.sectionId}-"]`);
        allLoaders.forEach((loader) => {
          loaderStates.set(loader.id, !loader.hidden);
        });

        this.allVariants.forEach((input) => {
          const variantId = input.getAttribute('data-quantity-variant-id');
          if (variantId) {
            // Check if there's a pending update for this variant
            const pendingUpdate = this.pendingUpdates.get(variantId);
            if (pendingUpdate) {
              states.set(variantId, {
                value: pendingUpdate.value,
                hasFocus: document.activeElement === input,
                selectionStart: input.selectionStart,
                selectionEnd: input.selectionEnd,
              });
            } else {
              states.set(variantId, {
                value: input.value,
                hasFocus: document.activeElement === input,
                selectionStart: input.selectionStart,
                selectionEnd: input.selectionEnd,
              });
            }
          }
        });

        return { inputStates: states, loaderStates };
      }

      restoreInputStates(capturedStates) {
        // Wait for DOM to be fully updated
        requestAnimationFrame(() => {
          const { inputStates, loaderStates } = capturedStates;

          // Restore input states
          this.allVariants.forEach((input) => {
            const variantId = input.getAttribute('data-quantity-variant-id');
            const state = inputStates.get(variantId);

            if (state) {
              // Only restore if the value is different from cart value
              // This preserves user input while respecting cart updates
              const pendingUpdate = this.pendingUpdates.get(variantId);
              if (pendingUpdate && pendingUpdate.value !== parseInt(input.value)) {
                input.value = state.value;
              }

              if (state.hasFocus) {
                input.focus();
                if (state.selectionStart !== null && state.selectionEnd !== null) {
                  input.setSelectionRange(state.selectionStart, state.selectionEnd);
                }
              }

              // Clear pending update after restoration
              this.pendingUpdates.delete(variantId);
            }
          });

          // Restore loader states if there are pending updates in queue
          if (this.isProcessingQueue || this.updateQueue.length > 0) {
            loaderStates.forEach((isVisible, loaderId) => {
              const loader = document.getElementById(loaderId);
              if (loader && isVisible) {
                loader.hidden = false;
              }
            });
          }
        });
      }

      executeUpdate(items, line, name, target) {
        // Loading already enabled in queueUpdate, no need to call again
        this.setErrorMessage();

        let pageUrl = this.getAttribute('data-page-url');

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const renderSectionId = this.getAttribute('data-render-section-id');

        const body = JSON.stringify({
          updates: items,
          sections: Array.from(new Set([...sectionsToBundle, this.sectionId, renderSectionId])), // Ensure current section is included
          // TODO , create a unique page only for rendering quick-order list row items, to render single product rows and inject them here, include the cart however.
          sections_url: pageUrl || this.getAttribute('data-product-url'),
        });

        fetch(`${theme.routes.cart_update_url}`, { ...theme.utils.fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'quick-order-list',
              cart: parsedState,
              target,
              line,
              name,
            });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.log(error);
              this.setErrorMessage(theme.cartStrings.error);
            }
            this.isProcessingQueue = false;
            this.processNextInQueue();
          });
      }

      removeAllItemsInCart() {
        // clear the entire cart, not just the items in the quick order list
        this.queueUpdate({ items: {}, line: null, name: null, target: null, isRemoveAll: true });
      }

      executeRemoveAll() {
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );
        const body = JSON.stringify({
          sections: sectionsToBundle,
          sections_url: this.getAttribute('data-page-url'),
        });

        fetch(`/cart/clear.js`, { ...theme.utils.fetchConfig(), ...{ body, method: 'POST' } })
          .then((response) => response.json())
          .then((parsedState) => {
            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              source: 'quick-order-list',
              cart: parsedState,
            });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.log(error);
              this.setErrorMessage(theme.cartStrings.error);
            }
            this.isProcessingQueue = false;
            this.processNextInQueue();
          });
      }

      updateMultipleQty(items, line, name, target) {
        this.queueUpdate({ items, line, name, target });
      }

      enableLoading(line) {
        const loaders = document.querySelectorAll(`[id^="Loader-${this.sectionId}-${line}"]`);
        loaders.forEach((loader) => (loader.hidden = false));

        const loaderAll = document.getElementById(`Loader-${this.sectionId}-all`);
        if (loaderAll) loaderAll.hidden = false;
      }

      disableLoading(line) {
        const loaders = document.querySelectorAll(`[id^="Loader-${this.sectionId}-${line}"]`);
        loaders.forEach((loader) => (loader.hidden = true));

        const loaderAll = document.getElementById(`Loader-${this.sectionId}-all`);
        if (loaderAll) loaderAll.hidden = true;
      }

      setErrorMessage(message = null) {
        const errorElement = this.querySelector('.quick-order-list__error');
        if (message) {
          const updatedMessageElement = errorElement.querySelector('.quick-order-list-error__message');
          updatedMessageElement.innerText = message;
          errorElement.removeAttribute('hidden');
        } else {
          errorElement.setAttribute('hidden', '');
        }
      }

      setValidity(target, message) {
        target.setCustomValidity(message);
        target.reportValidity();
        target.value = target.defaultValue;
        target.select();
      }

      validateQuantity(event) {
        const target = event.target;
        const inputValue = parseInt(target.value);
        const index = target.getAttribute('data-index');
        let message = '';

        if (inputValue < parseInt(target.getAttribute('data-min'))) {
          message = theme.quickOrderListStrings.minError.replace('[min]', target.getAttribute('data-min'));
        } else if (inputValue > parseInt(target.max)) {
          message = theme.quickOrderListStrings.maxError.replace('[max]', target.max);
        } else if (inputValue % parseInt(target.step) !== 0) {
          message = theme.quickOrderListStrings.stepError.replace('[step]', target.step);
        }

        if (message) {
          this.setValidity(target, message);
        } else {
          target.setCustomValidity('');
          target.reportValidity();
          this.updateQuantity(index, inputValue, document.activeElement.getAttribute('name'), target);
        }
      }
    },
    { extends: 'form' }
  );
}
